import { Component, Output, EventEmitter, signal, inject, ChangeDetectionStrategy } from '@angular/core';

import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { IconComponent } from '../../../../../shared/components/icon/icon.component';
import { IconType } from '../../../../../shared/services/icon.service';

@Component({
  selector: 'app-change-password',
  standalone: true,
  imports: [ReactiveFormsModule, IconComponent],
  templateUrl: './change-password.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './change-password.component.css',
})
export class ChangePasswordComponent {
  @Output() changePassword = new EventEmitter<{
    currentPassword: string;
    newPassword: string;
  }>();

  private fb = inject(FormBuilder);

  readonly IconType = IconType;
  isExpanded = signal(false);
  isSaving = signal(false);
  showCurrentPassword = signal(false);
  showNewPassword = signal(false);
  showConfirmPassword = signal(false);

  passwordForm: FormGroup;

  constructor() {
    this.passwordForm = this.fb.group(
      {
        currentPassword: ['', [Validators.required]],
        newPassword: ['', [Validators.required, Validators.minLength(8)]],
        confirmPassword: ['', [Validators.required]],
      },
      {
        validators: this.passwordValidator,
      }
    );
  }

  passwordValidator(control: AbstractControl): ValidationErrors | null {
    const currentPassword = control.get('currentPassword')?.value;
    const newPassword = control.get('newPassword')?.value;
    const confirmPassword = control.get('confirmPassword')?.value;

    const errors: ValidationErrors = {};

    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      errors['passwordMismatch'] = true;
    }

    if (currentPassword && newPassword && currentPassword === newPassword) {
      errors['samePassword'] = true;
    }

    return Object.keys(errors).length > 0 ? errors : null;
  }

  toggleExpanded(): void {
    this.isExpanded.update((v) => !v);
    if (!this.isExpanded()) {
      this.passwordForm.reset();
    }
  }

  togglePasswordVisibility(field: 'current' | 'new' | 'confirm'): void {
    switch (field) {
      case 'current':
        this.showCurrentPassword.update((v) => !v);
        break;
      case 'new':
        this.showNewPassword.update((v) => !v);
        break;
      case 'confirm':
        this.showConfirmPassword.update((v) => !v);
        break;
    }
  }

  onSubmit(): void {
    if (this.passwordForm.valid) {
      this.isSaving.set(true);
      this.changePassword.emit({
        currentPassword: this.passwordForm.value.currentPassword,
        newPassword: this.passwordForm.value.newPassword,
      });
    }
  }

  onSaveComplete(success: boolean): void {
    this.isSaving.set(false);
    if (success) {
      this.passwordForm.reset();
      this.isExpanded.set(false);
    }
  }

  hasError(field: string, error: string): boolean {
    const control = this.passwordForm.get(field);
    return !!(control?.touched && control?.hasError(error));
  }
}
